<template>
  <div class="json-editor">
    <JsonEditor
      v-model="editorValue"
      v-bind="editorProps"
      height="300"
    />
  </div>
</template>

<script lang="ts">
import { useColorMode } from '@vueuse/core'
import { computed, defineComponent, ref, watch } from 'vue'
import JsonEditor from 'vue3-ts-jsoneditor'

import type { JsonValue, MessageType } from '@protobuf-ts/runtime'
import type { PropType } from 'vue'
import type { ValidationError } from 'vue3-ts-jsoneditor'

export default defineComponent({
  name: 'ProgramMetadataField',

  components: {
    JsonEditor,
  },

  emits: {
    'update:modelValue': (_value: object | null) => true,
  },

  props: {
    message: { type: Object as PropType<MessageType<object>>, required: true },
    modelValue: { type: Object as PropType<object | null>, default: null },
  },

  setup (props, { emit, expose }) {
    const internalValue = ref<object>({})
    const editorValue = computed({
      get: () => props.message.toJson(internalValue.value, { emitDefaultValues: true, enumAsInteger: false }),
      set: (value: JsonValue) => {
        try {
          internalValue.value = props.message.fromJson(value, { ignoreUnknownFields: false })
          emit('update:modelValue', internalValue.value)
        } catch {
          console.warn('Failed to parse JSON input')
        }
      },
    })

    const editorErrors = ref<ValidationError[]>([])
    const editorValid = computed(() => editorErrors.value.length === 0)
    const { state: theme } = useColorMode()
    const editorProps = computed(() => ({
      mainMenuBar: false,
      navigationBar: false,
      statusBar: false,
      darkTheme: theme.value === 'dark',
      validator: (json: unknown) => {
        const errors: ValidationError[] = []
        try {
          const metadata = props.message.fromJson(json as string, { ignoreUnknownFields: false })
          if (!props.message.is(metadata)) {
            errors.push({
              message: 'Program Metadata does not match Protobuf schema.',
              path: [],
              severity: 'error' as ValidationError['severity'],
            })
          }
        } catch (e) {
          errors.push({
            message: `Invalid JSON: ${(e as Error).message}`,
            path: [],
            severity: 'error' as ValidationError['severity'],
          })
        }
        editorErrors.value = errors
        return errors
      },
    }))

    watch(
      () => props.modelValue,
      (newValue) => {
        internalValue.value = newValue ?? props.message.create()
      },
      { immediate: true },
    )

    expose({
      valid: editorValid,
    })

    return {
      editorProps,
      editorValue,
    }
  },
})
</script>
